import { defineComponent, type DirectiveArguments, h, resolveComponent, resolveDirective, Suspense, type VNode, withDirectives } from 'vue'
import { useRogue } from '../js/rogue'
import { optimizeCmsImages, removeEmptyHeadings } from '../js/helpers'

const linkRegex = /<a[^>]*href=["'](\/[^"']*)["']/gi
const attrRegex = /<span data-type="attribute" data-value="(.*?)">(.*?)<\/span>/gi

/**
 * Render a Roguebuilder JSON tree into a SISU / Vue.JS component
 * @param h - render function
 * @param block - JSON tree
 * @param product - IProduct for the current PDP
 * @param additionalVNodes - DEPRECATED Normally the children are all in the JSON. But if we need to add one at runtime, it will be passed in the children array
 *    Example:
 *    RenderBlock(:block="jsonObject") <-- The renderblock
 *      template(#heading)
 *        Heading {{__('Some Override Value')}} <-- This goes in `children` array, and will render into the `heading` slot of the RenderBlock
 * @param slot - (Recursive argument) Used to render this component into a specific slot of its parent component
 * @param lang - Current language - used for translations in EU store
 * @param segment - Current customer's primary segment
 * @param activeSimple - On a PDP, this will be set to the currently active product configured in the RHPA
 * @param rogue - Rogue object, for feature flags etc
 * @param selectedSimple - a object of a simple for a selected product
 * @returns {*}
 */
function renderBlock({
  block, product, lang = '', segment, rhpaConfig, rogue, selectedSimple,
}: {
  block: any,
  product: any,
  lang?: string,
  segment: any,
  rhpaConfig: any,
  rogue: any, selectedSimple: any
}): VNode {
  let sourceProps = (block || {}).props || {}

  // AB Tests can change the properties we render
  if (block.abTest && block.name !== 'Block' && rogue && rogue.featureFlags && rogue.featureFlags.eval) {
    try {
      const variant = rogue.featureFlags.eval(block.abTest.name)
      if ((block.abTest.variants || {})[variant]) {
        sourceProps = block.abTest.variants[variant]
      }
    } catch {
      console.warn('Error loading test')
    }
  }

  const props = Object.assign({}, sourceProps || {})

  if (product && typeof product !== 'string') {
    props.product = product
  }
  if (rhpaConfig) {
    props.rhpaConfig = rhpaConfig
  }
  if (selectedSimple && (block.name === 'GearSpecs' || block.name === 'GearSpecsWide') && block.props && block.props.dynamic) {
    props.selectedSimple = selectedSimple
  }

  // This is the Vue component data that will eventually be passed into the render function
  const data = {
    // Block.props in the JSON is a direct mapping of what the component is expecting for props. We add the `product` prop.
    ...props,
  }

  const directives: DirectiveArguments = []

  // block.class is a way to set static classes on the component
  if (block.class) {
    data.class = block.class
  }

  // check to see if we would like to track clicks on this block
  if (block.props && block.props.tracking && block.props.tracking.name) {
    data['data-track'] = block.props.tracking.name
    // if the data track cannot be found then it doesn't matter if we set the track index
    if (block.props.tracking.index) {
      data['data-index'] = block.props.tracking.index
    }
    if (block.props.tracking.whenVisible) {
      data['data-visibility'] = block.props.tracking.whenVisible
    }
  }

  // Styles should be pulled from `props.styles` and mapped to `style` vue property
  const styles = (block.props || {}).styles
  if (styles) {
    data.style = {}
    Object.keys(styles)
      .filter((key) => styles[key] !== undefined)
      .forEach((key) => data.style[key] = styles[key])
  }

  let html = ''
  if (block.name === 'RichContent') {
    html = (block.props || {}).html || ''
  } else if (block.name === 'html') {
    html = block.value || ''
  }

  // HTML nodes are "leaf" nodes (cant have children), so they are pretty straightforward
  if (block.name === 'html' || block.name === 'RichContent') {
    // html = html || block.value || (block.props || {}).html || ''
    // They can be translated via the Translate directive
    if (lang && lang.indexOf('en') !== 0) {
      const translateDirective = resolveDirective('translate')

      if (translateDirective) {
        directives.push([translateDirective, { ns: (product || {}).sku }])
      }
    }

    const routePrefix = (rogue?.l10n || {}).path
    if (routePrefix) {
      // Replace relative links with locale-relative links
      try {
        html = html.replace(linkRegex, (fullMatch, path) => (path.indexOf(routePrefix) === 0 ? fullMatch : fullMatch.replace(path, routePrefix + path)))
      } catch (e) {
        rogue.log.warn('Unable to replace links', e)
      }
    }

    if (selectedSimple || product) {
      try {
        // Replace data-type="attribute" spans
        html = html.replace(attrRegex, (_fullMatch, attr, dflt) => {
          return selectedSimple?.attributes?.[attr] || product?.attributes?.[attr] || dflt
        })
      } catch (e) {
        rogue.log.warn('Unable to replace attributes', e)
      }
    }

    // @todo Might be better to ALWAYS use divs, but need to check that `html` blocks aren't used as inline things ever
    return withDirectives(h(data.class ? 'div' : 'span', Object.assign({}, data, { innerHTML: optimizeCmsImages(removeEmptyHeadings(html)) })), directives)
  }

  // Children will be the third argument to render function - we need to recursively render VNodes for all the children of this block
  const blockChildren: any = {}

  // Some of the blocks actually pass the JSON for their children as a property into the parent, rather than render them as children components.
  // This allows the parent to have more control over how the children show up - for example, the gallery might only render the first 8 children, or even
  // change their properties before rendering them.
  if (block.name === 'InfiniteCarousel') {
    data.slides = block.slots?.default || []
  } else if (block.name === 'Gallery') {
    data.images = (block.slots?.images || []).filter((b: any) => b.props && b.props.config).map((b: any) => b.props.config)
  } else if (block.name === 'ProductTabs' || block.name === 'CMSTabs') {
    data.tabs = (block.slots?.default || []).filter((b: any) => b.props && b.props.tab)
    data.selectedSimple = selectedSimple
  } else if (block.name === 'Block' && block.abTest && rogue && rogue.featureFlags && rogue.featureFlags.eval) {
    let abSlot = 'default'
    try {
      abSlot = rogue.featureFlags.eval(block.abTest.name)
      if (!abSlot || !block.slots?.[abSlot]) {
        abSlot = 'default'
      }
    } catch (e) {
      console.log('Unable to evaluate feature flag', e)
    }
    const defaultSlotChildren: VNode[] = [];
    (block.slots?.[abSlot] || []).forEach((child: any) => {
      defaultSlotChildren.push(renderBlock({ block: child, product, segment, lang, rhpaConfig, rogue, selectedSimple }))
    })

    if (defaultSlotChildren.length > 0) {
      blockChildren.default = () => defaultSlotChildren
    }
  } else {
    // Loop through all of the defined slots of the JSON object and render the contents as VNodes
    Object.keys(block.slots || {})

      // Sidebars is a special slot that we will only render on-demand. We dont want to auto-render all those children when rendering the parent.
      .filter((slotName) => slotName !== 'sidebars')
      .forEach((slotName) => {
        const slotChildren: VNode[] = [];

        // block.slots[someSlot] is always an array of components that should go into that slot
        (block.slots[slotName] || [])
          // Hotfix: We dont want to render empty HTML blocks. So filter out any HTML blocks that have no `value`
          .filter((child: any) => child.name !== 'html' || child.value)
          .forEach((child: any) => {
            // If we pass in a segment, filter out blocks that should only be shown for other segments
            if (segment && child.segments && child.segments.indexOf(segment) === -1) {
              return
            }

            // Dont show a block based on SKU, if we are not looking at that SKU currently...
            if (child.skus && child.skus.indexOf(product.sku?.toUpperCase()) === -1 && child.skus.indexOf(selectedSimple?.sku.toUpperCase()) === -1) {
              return
            }

            // Render the block to a vnode. This is a recursive call, since the child may have children of its own
            const slotChild = renderBlock({ block: child, product, segment, lang, rhpaConfig, rogue, selectedSimple })

            // If the child is hidden (for a different segment maybe) it will return undefined and we dont want to render it
            if (slotChild) {
              // Push this child to the blockChildren array, which will be passed to the main block's render function
              slotChildren.push(slotChild)
            }
          })

        if (slotChildren.length > 0) {
          blockChildren[slotName] = () => slotChildren
        }
      })
  }

  // Some SISU components were renamed to avoid html element name conflicts
  // This is here to support any lingering json that has the old component names set.
  // Native html buttons should be all lowercase and won't be changed.
  if (['Button', 'Input', 'Select', 'Textarea'].includes(block.name)) {
    block.name = `Rogue${block.name}`
  }

  const componentType = resolveComponent(block.name)

  const renderedBlock = h(componentType, data, blockChildren)

  // SSR build sometimes has issues rendering async components with renderblock. Suspense is an
  // experimental vue component that fixes it.
  if (typeof componentType === "object" && componentType.name === 'AsyncComponentWrapper') {

    if (block.name === 'StrongestLeaderboard') {
      const frameHeight = (block.props || {}).frameHeight || '55'

      return h(Suspense, null, {
        default  : () => renderedBlock,
        fallback : () => h('div', {
          class : ['strongest-leaderboard-loading', data.class],
          style : { minHeight: `${frameHeight}vh`, display: 'flex', marginTop: '0', alignItems: 'center', justifyContent: 'center' },
        }, [h(resolveComponent('Spinner'))]),
      })
    }
    // InfiniteCarousel computes its own aspect-ratio reservation internally but can't run that before its own code has loaded.
    // Suspense covers that gap;
    if (block.name === 'InfiniteCarousel') {
      const firstSlideBackground = (((block.slots?.default || [])[0] || {}).props || {}).background

      // The fallback needs the same rg-show-*/rogue-hide visibility classes as the real
      // component (`data.class`, set above from `block.class`)
      return h(Suspense, null, {
        default  : () => renderedBlock,
        fallback : () => h(resolveComponent('InfiniteCarouselLoading'), { background: firstSlideBackground, class: data.class }),
      })
    }

    return h(Suspense, () => renderedBlock)
  } else {
    return renderedBlock
  }

  return renderedBlock
}

const RenderBlock = defineComponent({
  name   : 'RenderBlock',
  inject : {
    primarySegment: {
      from    : 'primarySegment',
      default : 'None',
    },
  },
  props: {
    block: {
      type     : [Object, Array],
      required : false,
    },
    product: {
      type    : Object,
      default : () => ({}),
    },
    rhpaConfig: {
      type    : Object,
      default : null,
    },
    selectedSimple: {
      type    : Object,
      default : null,
    },
  },
  // The render function is going to recursively call the renderBlock for the full roguebuilder JSON tree
  render() {
    // Product (and RHPA data if applicable) will be passed as a property into every child component, even if the block doesn't need it.
    // This allows blocks to hide / show themselves based on SKU of the active product, or things like gearspecs to know
    // what to show.
    const rogue = useRogue()

    if (!this.block || (typeof this.block === 'object' && !Object.keys(this.block).length)) {
      return undefined
    }

    const lang = rogue?.l10n ? rogue.l10n.lang : ''

    const segment = this.primarySegment || 'None'

    // We usually get a tree, but if we get an array of json trees, then we need to put them all into a parent container
    if (Array.isArray(this.block)) {
      const contents = this.block
        .filter((child: any) => {
          if (segment && child.segments && child.segments.indexOf(segment) === -1) {
            return false
          }

          // Dont show a block based on SKU, if we are not looking at that SKU currently...
          if (child.skus
            && child.skus.indexOf(this.product.sku?.toUpperCase()) === -1
            && child.skus.indexOf(this.selectedSimple?.sku.toUpperCase()) === -1
          ) {
            return false
          }

          return true
        })
        .map((blk) => renderBlock({
          block          : blk,
          segment,
          product        : this.product,
          lang,
          rhpaConfig     : this.rhpaConfig,
          rogue          : rogue || {},
          selectedSimple : this.selectedSimple,
        }))

      return h('div', contents)
    }

    return renderBlock({
      block          : this.block,
      segment,
      product        : this.product,
      lang,
      rhpaConfig     : this.rhpaConfig,
      rogue          : rogue || {},
      selectedSimple : this.selectedSimple,
    })
  },
})

export default RenderBlock